'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Checkbox, CircularProgress } from 'src/components';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { useInfiniteScroll } from 'src/hooks/common/useInfiniteScroll';
import type { AutocompleteOption, BaseFilterProps } from '../types';

interface FilterAutocompleteProps
  extends Pick<BaseFilterProps, 'value' | 'onChange'> {
  options: AutocompleteOption[];
  isLoading?: boolean;
  placeholder?: string;
  onSearch?: (term: string) => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
}

const FilterAutocomplete: React.FC<FilterAutocompleteProps> = ({
  options,
  value,
  onChange,
  isLoading,
  placeholder = 'Search...',
  onSearch,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
}) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [openAbove, setOpenAbove] = useState(false);

  const { fetchTriggerRef } = useInfiniteScroll({
    hasNextPage,
    isFetching: isFetchingNextPage,
    fetchNextPage: fetchNextPage ?? (() => undefined),
  });

  const updateDropdownPosition = () => {
    if (open && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();

      // Try to find the drawer container
      const drawerContainer =
        inputRef.current.closest('[role="dialog"]') ||
        inputRef.current.closest('.drawer') ||
        inputRef.current.closest('[data-testid*="drawer"]');

      let containerBottom = window.innerHeight;
      if (drawerContainer) {
        const drawerRect = drawerContainer.getBoundingClientRect();
        containerBottom = drawerRect.bottom;
      }

      const spaceBelow = containerBottom - rect.bottom;
      const minSpaceNeeded = 300; // Need 300px to open below (more conservative)

      // Check if should open above
      const shouldOpenAbove = spaceBelow < minSpaceNeeded;

      setOpenAbove(shouldOpenAbove);
    }
  };

  useEffect(() => {
    if (open) {
      // Small delay to ensure DOM is ready
      setTimeout(updateDropdownPosition, 0);

      const handleScroll = () => updateDropdownPosition();
      const handleResize = () => updateDropdownPosition();

      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setSearch('');
        onSearch?.('');
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, onSearch]);

  const handleSearch = (term: string) => {
    setSearch(term);
    onSearch?.(term);
    setOpen(true);
  };

  const handleOptionSelect = (option: AutocompleteOption) => {
    const newValues = value.values.some(
      (v) => String(v.value) === String(option.value)
    )
      ? value.values.filter((v) => String(v.value) !== String(option.value))
      : [...value.values, option];

    onChange({
      ...value,
      values: newValues,
    });
  };

  const handleToggleDropdown = () => {
    setOpen((prev) => !prev);
  };

  const listboxId = 'filter-autocomplete-listbox';

  return (
    <div className="relative flex-1 h-full" ref={containerRef}>
      <div className="flex items-center gap-2 w-full">
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          onClick={() => setOpen(true)}
          placeholder={placeholder}
          data-testid="filter-autocomplete-input"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          className="w-full bg-transparent outline-none text-sm text-primary placeholder:text-neutral-500 truncate"
        />
        <button
          type="button"
          className="w-4 h-4 flex items-center justify-center"
          onClick={handleToggleDropdown}
          aria-label={open ? 'Close dropdown' : 'Open dropdown'}
          data-testid="filter-autocomplete-toggle"
        >
          <Icon
            icon={faCaretDown}
            className={`
            text-neutral-600 w-4 h-4 transition-all duration-300 cursor-pointer ${
              open ? '-rotate-90' : 'rotate-0'
            }
            `}
          />
        </button>
      </div>

      {open && (
        <div
          ref={dropdownRef}
          id={listboxId}
          role="listbox"
          aria-label="Filter options"
          className={`absolute -right-4 w-80 bg-white border border-neutral-200 rounded-md shadow-lg max-h-48 overflow-y-auto overflow-x-hidden z-[9999] ${
            openAbove ? 'bottom-full mb-3' : 'top-full mt-3'
          }`}
          onMouseDown={(e) => e.stopPropagation()}
          data-testid="filter-autocomplete-dropdown"
        >
          {isLoading ? (
            <div
              className="p-3 flex items-center justify-center gap-2 text-sm"
              data-testid="filter-autocomplete-loading"
            >
              <CircularProgress size="sm" color="neutral" />
              <span className="text-neutral-600 font-medium">Loading...</span>
            </div>
          ) : options?.length > 0 ? (
            <ul className="p-2">
              {options.map((option) => {
                const isSelected = value.values.some(
                  (v) => String(v.value) === String(option.value)
                );
                return (
                  <li
                    key={option.value}
                    role="option"
                    aria-selected={isSelected}
                    className="px-3 py-2 text-sm text-neutral-900 hover:bg-neutral-50 cursor-pointer flex gap-2 transition-colors duration-150"
                    onClick={() => handleOptionSelect(option)}
                    data-testid={`filter-autocomplete-option-${option.value}`}
                  >
                    <Checkbox
                      size="sm"
                      variant="soft"
                      color="primary"
                      checked={isSelected}
                      onChange={() => handleOptionSelect(option)}
                    />
                    <span className="text-sm break-words">{option.label}</span>
                  </li>
                );
              })}
              {isFetchingNextPage && (
                <li className="px-3 py-2 flex items-center justify-center">
                  <CircularProgress size="sm" color="neutral" />
                </li>
              )}
              <li
                ref={fetchTriggerRef}
                className="h-1"
                aria-hidden="true"
                data-testid="filter-autocomplete-scroll-trigger"
              />
            </ul>
          ) : (
            <div
              className="p-3 text-sm text-neutral-500 text-center"
              data-testid="filter-autocomplete-empty"
            >
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterAutocomplete;
